import {
	Close as CloseIcon,
	Comment as CommentIcon,
	DirectionsCar as DirectionsCarIcon,
	Event as EventIcon,
	FilterList as FilterListIcon,
	Inventory2 as Inventory2Icon,
	Pause as PauseIcon,
	PlayArrow as PlayArrowIcon,
	Search as SearchIcon,
	SkipPrevious as SkipPreviousIcon,
	TableChart as TableChartIcon,
	Visibility as VisibilityIcon,
} from "@mui/icons-material";
import {
	Box,
	Button,
	Chip,
	IconButton,
	LinearProgress,
	Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import Joyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";

/**
 * Vehicle Tracking (VFF) Dashboard guided tour.
 *
 * Reuses the exact tour architecture and UI design of {@link VFFJoyrideTour}
 * (react-joyride + custom dark-navy tooltip, progress bar, and
 * Previous / Next / Skip Tour / Pause / Close controls).
 *
 * Flow: Welcome screen -> NEXT -> Total Vehicles -> Total Parts -> View ->
 * Filters -> Complete.
 *
 * The Welcome screen is a separate intro and is NOT counted as a feature step:
 * the step counter and progress bar reflect the 4 feature steps only.
 */
const VffDashboardJoyrideTour = ({ isOpen, onClose }) => {
	const [run, setRun] = useState(false);
	const [stepIndex, setStepIndex] = useState(0);
	const [isPlaying, setIsPlaying] = useState(true);

	// Step 0 is the Welcome screen (no highlight). Steps 1-4 are the feature
	// steps that highlight actual dashboard elements.
	const steps = [
		{
			target: "body",
			content: (
				<Box sx={{ p: 3, maxWidth: 450 }}>
					<Typography
						variant="h5"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<TableChartIcon sx={{ color: "#cc0000" }} />
						Vehicle Tracking Tour
					</Typography>
					<Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
						Welcome to the Vehicle Tracking module - your comprehensive tool for
						managing pre-series validation and component testing in the MAN
						development process.
					</Typography>
					<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
						<Chip
							icon={<FilterListIcon />}
							label="Advanced Filtering"
							size="small"
							sx={{ bgcolor: "#e3f2fd" }}
						/>
						<Chip
							icon={<SearchIcon />}
							label="Smart Search"
							size="small"
							sx={{ bgcolor: "#e8f5e8" }}
						/>
						<Chip
							icon={<CommentIcon />}
							label="Collaboration"
							size="small"
							sx={{ bgcolor: "#fff3e0" }}
						/>
						<Chip
							icon={<EventIcon />}
							label="Date Management"
							size="small"
							sx={{ bgcolor: "#fce4ec" }}
						/>
					</Box>
					<Typography
						variant="body2"
						sx={{ color: "#666", fontStyle: "italic" }}
					>
						This tour will guide you through all key features in about 3
						minutes.
					</Typography>
				</Box>
			),
			placement: "center",
			disableBeacon: true,
			styles: {
				options: {
					zIndex: 10000,
				},
			},
		},
		{
			target: '[data-tour-target="vff-dash-total-vehicles"]',
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<DirectionsCarIcon sx={{ color: "#cc0000" }} />
						Total Vehicles
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						Shows the total number of unique vehicles in the Vehicle Tracking
						dashboard.
					</Typography>
				</Box>
			),
			placement: "bottom",
			spotlightPadding: 8,
			disableScrolling: true,
		},
		{
			target: '[data-tour-target="vff-dash-total-parts"]',
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<Inventory2Icon sx={{ color: "#cc0000" }} />
						Total Parts
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						Shows the total number of parts across all vehicles.
					</Typography>
				</Box>
			),
			placement: "bottom",
			spotlightPadding: 8,
			disableScrolling: true,
		},
		{
			target: '[data-tour-target="vff-dash-view"]',
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<VisibilityIcon sx={{ color: "#cc0000" }} />
						View
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						Switch between different views to analyze vehicle and part status
						data.
					</Typography>
					<Box
						sx={{
							bgcolor: "#f8f9fa",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #e0e0e0",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#cc0000" }}
						>
							Available Views:
						</Typography>
						<Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
							• Vehicle • Timeline • Timeline by Earliest
						</Typography>
					</Box>
				</Box>
			),
			placement: "bottom",
			spotlightPadding: 8,
			disableScrolling: true,
		},
		{
			target: '[data-tour-target="vff-dash-filters"]',
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<FilterListIcon sx={{ color: "#cc0000" }} />
						Filters
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						Use filters to narrow down the displayed vehicle tracking data.
						Reset clears the selections and Apply updates the results.
					</Typography>
					<Box
						sx={{
							bgcolor: "#f8f9fa",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #e0e0e0",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#cc0000" }}
						>
							Filter Options:
						</Typography>
						<Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
							• All Programs • All Vehicles • All Part Numbers • All Purchase
							Types • Reset • Apply
						</Typography>
					</Box>
				</Box>
			),
			placement: "bottom-end",
			spotlightPadding: 8,
			disableScrolling: true,
		},
	];

	// Number of feature steps (everything after the Welcome screen).
	const featureStepCount = steps.length - 1;
	const isWelcome = stepIndex === 0;
	// 1-based position of the current feature step (0 while on the Welcome screen).
	const featureStepNumber = isWelcome ? 0 : stepIndex;
	// Progress reflects the feature steps only; the Welcome screen sits at 0%.
	const progress = isWelcome ? 0 : (featureStepNumber / featureStepCount) * 100;

	useEffect(() => {
		if (isOpen) {
			setRun(true);
			setStepIndex(0);
			setIsPlaying(true);
		} else {
			setRun(false);
		}
	}, [isOpen]);

	const handleJoyrideCallback = useCallback(
		(data) => {
			const { action, index, status, type } = data;

			if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
				const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);
				setStepIndex(nextStepIndex);
			} else if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
				setRun(false);
				onClose();
			}
		},
		[onClose],
	);

	const handleClose = () => {
		// The close (X) button skips the entire tour.
		setRun(false);
		onClose();
	};

	const handleSkipTour = () => {
		setRun(false);
		onClose();
	};

	const handlePlay = () => {
		setIsPlaying(!isPlaying);
		setRun(!isPlaying);
	};

	// Custom tooltip component (mirrors VFFJoyrideTour's design).
	const CustomTooltip = ({
		index,
		step,
		backProps,
		primaryProps,
		tooltipProps,
	}) => (
		<Box
			{...tooltipProps}
			sx={{
				backgroundColor: "white",
				borderRadius: "12px",
				boxShadow: "0 8px 32px rgba(30, 40, 50, 0.15)",
				maxWidth: step.content.props.sx?.maxWidth || 400,
				overflow: "hidden",
				fontFamily: "MAN EUROPE_Regular",
			}}
		>
			{/* Header */}
			<Box
				sx={{
					backgroundColor: "#1e2832",
					color: "white",
					p: 2,
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<Typography
					variant="h6"
					sx={{
						fontWeight: "bold",
						fontSize: "14px",
						fontFamily: "MAN EUROPE_Bold",
					}}
				>
					{index === 0
						? "Vehicle Tracking Tour"
						: `Vehicle Tracking Tour (${index}/${featureStepCount})`}
				</Typography>
				<Box sx={{ display: "flex", gap: 1 }}>
					<IconButton
						{...backProps}
						size="small"
						disabled={index === 0}
						sx={{ color: index === 0 ? "#666" : "white" }}
					>
						<SkipPreviousIcon fontSize="small" />
					</IconButton>
					<IconButton onClick={handlePlay} size="small" sx={{ color: "white" }}>
						{isPlaying ? (
							<PauseIcon fontSize="small" />
						) : (
							<PlayArrowIcon fontSize="small" />
						)}
					</IconButton>
					<IconButton
						onClick={handleClose}
						size="small"
						sx={{ color: "white" }}
						title="Skip Tour"
					>
						<CloseIcon fontSize="small" />
					</IconButton>
				</Box>
			</Box>
			{/* Progress Bar */}
			<Box sx={{ px: 2, pt: 1 }}>
				<LinearProgress
					variant="determinate"
					value={progress}
					sx={{
						height: 4,
						borderRadius: 2,
						backgroundColor: "#e0e0e0",
						"& .MuiLinearProgress-bar": {
							backgroundColor: "#cc0000",
						},
					}}
				/>
				<Typography
					variant="caption"
					sx={{
						display: "block",
						textAlign: "center",
						mt: 0.5,
						color: "#666",
						fontSize: "12px",
					}}
				>
					{index === 0
						? "Welcome"
						: `Step ${featureStepNumber} of ${featureStepCount}`}
				</Typography>
			</Box>
			{/* Content */}
			<Box sx={{ p: 0 }}>{step.content}</Box>
			{/* Footer */}
			<Box
				sx={{
					p: 2,
					borderTop: "1px solid #eee",
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					backgroundColor: "#f8f9fa",
				}}
			>
				<Button
					onClick={handleSkipTour}
					variant="text"
					sx={{
						color: "#666",
						fontSize: "12px",
						fontFamily: "MAN EUROPE_Regular",
					}}
				>
					Skip Tour
				</Button>
				<Box sx={{ display: "flex", gap: 1 }}>
					{index > 0 && (
						<Button
							{...backProps}
							variant="outlined"
							size="small"
							sx={{
								borderColor: "#cc0000",
								color: "#cc0000",
								fontSize: "12px",
								fontFamily: "MAN EUROPE_Bold",
								"&:hover": {
									borderColor: "#aa0000",
									backgroundColor: "#ffebee",
								},
							}}
						>
							Previous
						</Button>
					)}
					<Button
						{...primaryProps}
						variant="contained"
						size="small"
						sx={{
							backgroundColor: "#cc0000",
							fontSize: "12px",
							fontFamily: "MAN EUROPE_Bold",
							"&:hover": {
								backgroundColor: "#aa0000",
							},
						}}
					>
						{index === steps.length - 1 ? "Finish" : "Next"}
					</Button>
				</Box>
			</Box>
		</Box>
	);

	return (
		<>
			{/* Custom CSS to fix spotlight visibility */}
			<style>{`
        .__floater__arrow polygon {
          fill: #fff !important;
        }

        /* Fix spotlight visibility - remove grey blur */
        [data-tour-elem="spotlight"] {
          background-color: transparent !important;
          filter: none !important;
          opacity: 1 !important;
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
        }

        .react-joyride__spotlight {
          background-color: transparent !important;
          filter: none !important;
          opacity: 1 !important;
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
          mix-blend-mode: normal !important;
          border: none !important;
          box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.4),
            0 0 16px 3px rgba(255, 255, 255, 0.4) !important;
        }

        .react-joyride__overlay {
          mix-blend-mode: normal !important;
          background-color: rgba(0, 0, 0, 0.4) !important;
        }

        /* Ensure target elements are fully visible */
        .react-joyride__spotlight > * {
          filter: none !important;
          opacity: 1 !important;
          visibility: visible !important;
        }

        /* Remove any filters that might cause blur */
        body.react-joyride__body--scrolling * {
          filter: none !important;
        }

        /* Ensure proper z-index for highlighted elements */
        .react-joyride__spotlight {
          z-index: 9998 !important;
        }

        .react-joyride__tooltip {
          z-index: 9999 !important;
        }
      `}</style>
			<Joyride
				callback={handleJoyrideCallback}
				continuous={true}
				run={run && isPlaying}
				scrollToFirstStep={true}
				showProgress={false}
				showSkipButton={true}
				stepIndex={stepIndex}
				steps={steps}
				tooltipComponent={CustomTooltip}
				disableScrolling={true}
				spotlightClicks={true}
				disableOverlayClose={true}
				styles={{
					options: {
						arrowColor: "#fff",
						backgroundColor: "#fff",
						overlayColor: "rgba(0, 0, 0, 0.4)",
						primaryColor: "#cc0000",
						textColor: "#333",
						width: undefined,
						zIndex: 10000,
					},
					overlay: {
						mixBlendMode: "normal",
						backgroundColor: "rgba(0, 0, 0, 0.4)",
					},
					spotlight: {
						borderRadius: "8px",
						border: "none",
						backgroundColor: "transparent",
						boxShadow:
							"0 0 0 9999px rgba(0, 0, 0, 0.4), 0 0 16px 3px rgba(255, 255, 255, 0.4)",
						filter: "none !important",
						opacity: 1,
						mixBlendMode: "normal",
					},
					spotlightLegacy: {
						borderRadius: "8px",
						backgroundColor: "transparent",
						filter: "none",
					},
				}}
				locale={{
					back: "Previous",
					close: "Close",
					last: "Finish",
					next: "Next",
					skip: "Skip Tour",
				}}
			/>
		</>
	);
};

export default VffDashboardJoyrideTour;
